import React, { useEffect, useRef, useState } from "react";
import { getTextWidth } from "senf-shared/utils/helpers";
import { cn } from "../../util/classNameConstructor";

// Extracted TruncateMiddle component
const TruncateMiddle = React.memo<{
  text: string;
  className?: string;
}>(({ text, className = "" }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [displayText, setDisplayText] = useState(text);
  const [initialClassName, setInitialClassName] = useState("truncate");

  useEffect(() => {
    const updateTruncation = () => {
      if (!containerRef.current) return;

      const container = containerRef.current;
      const containerWidth = container.clientWidth;
      const { fontSize, fontFamily } = window.getComputedStyle(container);

      // If text fits entirely, don't truncate
      const fullTextWidth = getTextWidth(text, `${fontSize} ${fontFamily}`);
      if (fullTextWidth <= containerWidth) {
        setDisplayText(text);
        return;
      }

      // Binary search to find the maximum number of characters that can fit
      let low = 2; // Minimum 2 characters (1 on each side)
      let high = text.length - 2;
      let bestFit = 2;

      while (low <= high) {
        // Try with this many characters (half from start, half from end)
        const mid = Math.floor((low + high) / 2);
        const charsFromEachSide = Math.floor(mid / 2);

        const start = text.slice(0, charsFromEachSide);
        const end = text.slice(-charsFromEachSide);
        const truncated = `${start}...${end}`;

        const truncatedWidth = getTextWidth(
          truncated,
          `${fontSize} ${fontFamily}`,
        );

        if (truncatedWidth <= containerWidth) {
          bestFit = mid;
          low = mid + 1; // Try to fit more characters
        } else {
          high = mid - 1; // Try fewer characters
        }
      }

      // Apply the best fit
      const charsFromEachSide = Math.floor(bestFit / 2);
      const start = text.slice(0, charsFromEachSide);
      const end = text.slice(-charsFromEachSide);
      setDisplayText(`${start}...${end}`);
    };

    // Update on mount and when text changes
    updateTruncation();

    // Use ResizeObserver to detect container size changes
    const resizeObserver = new ResizeObserver(updateTruncation);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    setInitialClassName("");

    return () => {
      resizeObserver.disconnect();
    };
  }, [text]);

  return (
    <div
      ref={containerRef}
      className={cn(`whitespace-nowrap ${initialClassName}`, className)}
    >
      {displayText}
    </div>
  );
});

export default TruncateMiddle;
